import clsx from 'clsx';
import * as React from 'react';
import { shallowEqualArrays } from 'shallow-equal';

import {
    getLastEdit,
    listUsersInEdits,
    type RevisionPage,
    type RevisionPageDocument,
    RevisionPageType,
} from '@gitbook/hive-core';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Skeleton } from '@gitbook/spine-react';

import { usePageLayoutOptions } from '../Space';
import { UserAvatarStack, UserAvatarStackSkeleton } from '../Users';

const USER_AVATAR_SIZE = 'md';

/**
 * List the contributors and last edit of a page.
 */
export const PageMetadata = React.memo(function PageMetadata(props: {
    page: RevisionPage;
    className?: string;
}) {
    const { page, className } = props;

    const pageOptions = usePageLayoutOptions(
        page.type === RevisionPageType.Document ? (page as RevisionPageDocument) : null
    );

    const lastEdit = getLastEdit(page.edits);
    const contributors = listUsersInEdits(page.edits);
    const hasContributors = contributors.length > 0;

    if (!hasContributors && !lastEdit) {
        return null;
    }

    return (
        <PageMetadataContainer className={className}>
            {hasContributors ? <Contributors users={contributors} /> : <div />}
            {lastEdit && pageOptions.metadata !== false && (
                <LastModified date={lastEdit.updatedAt} />
            )}
        </PageMetadataContainer>
    );
});

function PageMetadataContainer(props: React.ComponentPropsWithoutRef<'div'>) {
    return (
        <div
            {...props}
            className={clsx('flex items-center justify-between gap-4', props.className)}
        />
    );
}

const Contributors = React.memo(
    function Contributors(props: { users: string[] }) {
        return <UserAvatarStack users={props.users} max={3} size={USER_AVATAR_SIZE} />;
    },
    (prev, next) => shallowEqualArrays(prev.users, next.users)
);

const LastModified = React.memo(function LastModified(props: { date: number | string }) {
    return (
        <div className="text-muted">
            Last modified <DateShortRelative value={new Date(props.date)} onlyDate={false} />
        </div>
    );
});

export function PageMetadataSkeleton() {
    return (
        <PageMetadataContainer>
            <UserAvatarStackSkeleton size={USER_AVATAR_SIZE} />
            <Skeleton>
                <LastModified date={Date.now()} />
            </Skeleton>
        </PageMetadataContainer>
    );
}
